import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import { useContext } from 'react';
import { useTextColor } from '../../styles/jss/utils';
import Link from '../Link/Link';
import useStyles from './ContentReference.styles';
import { SiteContext } from '../../utils/common';
import { useLoaded } from '../MainContent/useComponentLoading';
import SplitContentBySlash from '../../utils/SplitContentBySlash';
import RawHtml from '../RawHtml/RawHtml';
const ContentReference = (props) => {
    var _a, _b, _c;
    const { heading, message, links, textAlignment = 'LEFT', titleTextVariant = 'STANDARD', backgroundAndTextColours, backgroundColour, } = props; // To make sure we handle any nulls from graphQL
    // Remove spaces from Amplience
    const { isEnabledGoldCTA } = props;
    const { isFlyingFlowers } = useContext(SiteContext);
    const { textColour, backgroundColour: bgColorFromProps } = (backgroundAndTextColours === null || backgroundAndTextColours === void 0 ? void 0 : backgroundAndTextColours[0]) || {};
    const isNewBrandChange = Boolean((_a = isEnabledGoldCTA !== null && isEnabledGoldCTA !== void 0 ? isEnabledGoldCTA : bgColorFromProps) !== null && _a !== void 0 ? _a : textColour);
    const linksCount = (_b = links === null || links === void 0 ? void 0 : links.length) !== null && _b !== void 0 ? _b : 0;
    const { classes } = useStyles({
        linksCount,
        textColour: (_c = backgroundAndTextColours === null || backgroundAndTextColours === void 0 ? void 0 : backgroundAndTextColours[0]) === null || _c === void 0 ? void 0 : _c.textColour,
        textAlignment: textAlignment !== null && textAlignment !== void 0 ? textAlignment : 'LEFT',
    });
    // Helper function to get title text variant background color
    const getTitleVariantBackground = () => {
        if (bgColorFromProps)
            return bgColorFromProps;
        return null;
    };
    function determineBgColor() {
        var _a, _b, _c;
        if (isFlyingFlowers) {
            return ((_a = backgroundColour === null || backgroundColour === void 0 ? void 0 : backgroundColour[0]) === null || _a === void 0 ? void 0 : _a.backgroundColour) || '#FFFFFF';
        }
        if (titleTextVariant) {
            return getTitleVariantBackground() || ((_b = backgroundColour === null || backgroundColour === void 0 ? void 0 : backgroundColour[0]) === null || _b === void 0 ? void 0 : _b.backgroundColour) || '#FFFFFF';
        }
        return ((_c = backgroundColour === null || backgroundColour === void 0 ? void 0 : backgroundColour[0]) === null || _c === void 0 ? void 0 : _c.backgroundColour) || '#FFFFFF';
    }
    let bgColor = determineBgColor();
    // Always call hooks unconditionally with proper fallbacks
    const { textColor, backgroundColor } = useTextColor(bgColor);
    const resolveHeadingTitle = (heading, isFlyingFlowers, headingLineClass) => {
        return !isFlyingFlowers ? (_jsx(SplitContentBySlash, { content: heading, classNames: { headingLine: headingLineClass } })) : (heading);
    };
    const alignmentMapping = {
        LEFT: classes.textLeft,
        CENTER: classes.textCenter,
    };
    const textAlignmentClass = isFlyingFlowers
        ? classes.textCenter
        : alignmentMapping[textAlignment !== null && textAlignment !== void 0 ? textAlignment : ''] || classes.textLeft;
    // Helper function to get link container classes
    const getLinkContainerClasses = () => {
        const baseClasses = [classes.container];
        if (isFlyingFlowers) {
            baseClasses.push(classes.linkContainerInline);
        }
        else {
            baseClasses.push(isNewBrandChange ? classes.brandLinkContainer : classes.nonBrandLinksContentContainer);
        }
        return clsx(baseClasses);
    };
    // Helper function to get individual link classes
    const getLinkClasses = () => {
        const baseClasses = [classes.link];
        if (isFlyingFlowers) {
            baseClasses.push(classes.linkContainer);
        }
        else {
            baseClasses.push(isNewBrandChange ? classes.brandLink : classes.nonBrandLink);
        }
        return clsx(baseClasses);
    };
    // Helper function to render links container
    const renderLinksContainer = () => (_jsx("div", { className: getLinkContainerClasses(), children: links === null || links === void 0 ? void 0 : links.map((link, index) => (_jsx(Link, Object.assign({}, link, { button: true,
            variant: 'tertiary',
            thin: true,
            className: getLinkClasses(),
            icon: isFlyingFlowers ? 'sentiment_very_satisfied' : undefined }), `${link.label}_${index}`))) }));
    const isLargeAdvertising = titleTextVariant === 'LARGE_ADVERTISING' && !isFlyingFlowers;
    const headingTitle = resolveHeadingTitle(props.heading, isFlyingFlowers, classes.headingLine);
    // Helper function to get heading classes
    const getHeadingClasses = (includeColor = false) => {
        const baseClasses = [
            classes.heading,
            !isFlyingFlowers && (isLargeAdvertising ? classes.largeTitle : classes.standardTitle),
        ];
        return clsx(baseClasses);
    };
    // Helper function to render heading element
    const renderHeading = () => {
        const shouldUseTextColor = isLargeAdvertising && textColour;
        if (shouldUseTextColor) {
            return (_jsx("h3", { className: getHeadingClasses(), style: { color: textColour }, children: headingTitle }));
        }
        return _jsx("h3", { className: getHeadingClasses(true), children: headingTitle });
    };
    useLoaded(props);
    return (_jsx("div", { className: clsx(classes.container), style: { backgroundColor: backgroundColor, color: textColor }, "data-testid": "content-reference", children: _jsx("div", { className: clsx(classes.content, textAlignmentClass), children: _jsxs("div", { className: classes.contentInner, children: [heading && renderHeading(), message && _jsx(RawHtml, { className: classes.message, content: message }), !!(links === null || links === void 0 ? void 0 : links.length) && renderLinksContainer()] }) }) }));
};
export default ContentReference;
